import { ModelFramework } from '@viamrobotics/web-client/app/mltraining/v1/ml_training';

import type { RegistryItemSummary } from '$lib/registry-item';
import {
  type APITriplet,
  joinResourceTriplet,
  type ModelTriplet,
} from '$lib/resource-type';

import { isBuiltinAPI } from './api-types';
import { ServiceSubtypes } from './services';

export enum KnownMLModelServiceModel {
  TFLiteCPUDeprecated = 'rdk:builtin:tflite_cpu',
  TFLiteCPU = 'viam:mlmodel-tflite:tflite_cpu',
  TritonModule = 'viam:mlmodelservice:triton',
  OnnxCPU = 'viam:mlmodel:onnx-cpu',
  TensorFlowCPU = 'viam:mlmodel:tensorflow-cpu',
  PyTorchCPU = 'viam:mlmodel:torch-cpu',
}

export enum KnownMLModelModuleIDs {
  TFLiteCPU = 'viam:tflite_cpu',
  OnnxCPU = 'viam:onnx-cpu',
  TensorFlowCPU = 'viam:tensorflow-cpu',
  PyTorchCPU = 'viam:torch-cpu',
}

export const ML_MODEL_SERVICE_API = 'rdk:service:mlmodel';

/** `model_source` selects a registry deploy, which writes `model_path` only. */
export type MLModelPackageAttribute = 'package_reference' | 'model_source';

export interface MLModelDeployment {
  serviceModel: string;
  moduleID: string;
  /**
   * API triplet of the resource to create. For registry this is the API on the
   * module's registry item for that model name. It does not have to be
   * rdk:service:mlmodel.
   */
  api: string;
  /** Attribute that carries the package id. */
  packageAttribute?: MLModelPackageAttribute;
}

export interface RegistryModuleModel {
  model: string;
  api: string;
}

/**
 * Model entry from a module registry item. The API is whatever that model
 * declares and does not have to be an ML model service. `modelName` is the
 * deployment triplet, matched against the registry item's model field.
 */
export const registryModuleModel = (
  models: readonly RegistryModuleModel[] | undefined,
  modelName: string
): RegistryModuleModel | undefined => {
  if (!modelName) {
    return undefined;
  }
  const match = models?.find((entry) => entry.model === modelName);
  if (!match?.api) {
    return undefined;
  }
  return { api: match.api, model: match.model };
};

export interface MLModelDeploymentOptions {
  deploymentModularResource?: string;
  api?: string;
}

/** RDK model triplets are namespace:family:name, and each part matches [\w-]+. */
const rdkModelTripletPattern =
  /^(?<namespace>[\w-]+):(?<moduleName>[\w-]+):(?<name>[\w-]+)$/u;

/**
 * Fallback module id when search has no exact model match.
 * The registry item id is not always the triplet with the model name removed:
 * viam:mlmodel-tflite:tflite_cpu is provided by viam:tflite_cpu.
 */
export const moduleIDFromModelTriplet = (
  triplet: string
): string | undefined => {
  const match = rdkModelTripletPattern.exec(triplet);
  const namespace = match?.groups?.namespace;
  const moduleName = match?.groups?.moduleName;
  if (!namespace || !moduleName) {
    return undefined;
  }
  return `${namespace}:${moduleName}`;
};

export const isDeploymentModularResourceTriplet = (triplet: string): boolean =>
  moduleIDFromModelTriplet(triplet) !== undefined;

export const DEPLOYMENT_MODULAR_RESOURCE_ERROR =
  'Enter a colon-delimited triplet, for example viam-labs:arm-policy:policy';

/** Trims a registry triplet list and reports when it is empty or invalid. */
export const normalizeDeploymentModularResources = (
  triplets: readonly string[]
): { resources: string[]; error: string } => {
  const resources = triplets.map((triplet) => triplet.trim()).filter(Boolean);
  if (
    resources.length === 0 ||
    resources.some((triplet) => !isDeploymentModularResourceTriplet(triplet))
  ) {
    return { resources, error: DEPLOYMENT_MODULAR_RESOURCE_ERROR };
  }
  return { resources, error: '' };
};

/**
 * Deploy target for a registry framework: the module model triplet plus the
 * API that model currently exposes.
 */
export const mlModelDeploymentForRegistry = (
  deploymentModularResource: string | undefined,
  api: string | undefined,
  moduleID?: string
): MLModelDeployment | undefined => {
  if (!deploymentModularResource || !api) {
    return undefined;
  }
  const resolvedModuleID =
    moduleID ?? moduleIDFromModelTriplet(deploymentModularResource);
  if (!resolvedModuleID) {
    return undefined;
  }
  return {
    serviceModel: deploymentModularResource,
    moduleID: resolvedModuleID,
    api,
    packageAttribute: 'model_source',
  };
};

/** Distinct module ids for a list of model triplets, in first-seen order. */
export const moduleIDsFromTriplets = (
  triplets: readonly string[]
): string[] => {
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const triplet of triplets) {
    const moduleID = moduleIDFromModelTriplet(triplet);
    if (!moduleID || seen.has(moduleID)) {
      continue;
    }
    seen.add(moduleID);
    ids.push(moduleID);
  }
  return ids;
};

export interface RegistryDeploymentTarget {
  triplet: string;
  /** Registry item id. Omit to fall back to moduleIDFromModelTriplet. */
  moduleID?: string;
}

export type RegistryDeploymentInput = string | RegistryDeploymentTarget;

const deploymentTriplet = (input: RegistryDeploymentInput): string =>
  typeof input === 'string' ? input : input.triplet;

const deploymentModuleID = (
  input: RegistryDeploymentInput
): string | undefined => {
  if (typeof input !== 'string' && input.moduleID) {
    return input.moduleID;
  }
  return moduleIDFromModelTriplet(deploymentTriplet(input));
};

/**
 * One deploy target per triplet. Returns undefined when any triplet is missing
 * from its module registry item, so the caller can keep the action disabled.
 * Pass moduleID from the registry item when it is not namespace:family.
 */
export const mlModelDeploymentsForRegistry = (
  triplets: readonly RegistryDeploymentInput[],
  modelsByModuleID: ReadonlyMap<
    string,
    readonly RegistryModuleModel[] | undefined
  >
): MLModelDeployment[] | undefined => {
  if (triplets.length === 0) {
    return undefined;
  }
  const deployments: MLModelDeployment[] = [];
  for (const input of triplets) {
    const triplet = deploymentTriplet(input);
    const moduleID = deploymentModuleID(input);
    const model = moduleID
      ? registryModuleModel(modelsByModuleID.get(moduleID), triplet)
      : undefined;
    const deployment = mlModelDeploymentForRegistry(
      model?.model,
      model?.api,
      moduleID
    );
    if (!deployment) {
      return undefined;
    }
    deployments.push(deployment);
  }
  return deployments;
};

const knownDeployment = (
  serviceModel: string,
  moduleID: string
): MLModelDeployment => ({
  serviceModel,
  moduleID,
  api: ML_MODEL_SERVICE_API,
});

/**
 * Preferred resource + the module that provides it for a given model
 * framework, used to auto-configure a machine from the registry model page.
 * Returns undefined for frameworks we can't deploy (button is disabled).
 * Registry needs the module model triplet and the API read from that module.
 */
export const mlModelDeploymentForFramework = (
  framework: ModelFramework,
  options?: MLModelDeploymentOptions
): MLModelDeployment | undefined => {
  switch (framework) {
    case ModelFramework.TFLITE: {
      return knownDeployment(
        KnownMLModelServiceModel.TFLiteCPU,
        KnownMLModelModuleIDs.TFLiteCPU
      );
    }
    case ModelFramework.ONNX: {
      return knownDeployment(
        KnownMLModelServiceModel.OnnxCPU,
        KnownMLModelModuleIDs.OnnxCPU
      );
    }
    case ModelFramework.TENSORFLOW: {
      return knownDeployment(
        KnownMLModelServiceModel.TensorFlowCPU,
        KnownMLModelModuleIDs.TensorFlowCPU
      );
    }
    case ModelFramework.PYTORCH: {
      return knownDeployment(
        KnownMLModelServiceModel.PyTorchCPU,
        KnownMLModelModuleIDs.PyTorchCPU
      );
    }
    case ModelFramework.REGISTRY: {
      return mlModelDeploymentForRegistry(
        options?.deploymentModularResource,
        options?.api
      );
    }
    default: {
      return undefined;
    }
  }
};

export const MLModelServiceTypeToAllowedFrameworks: Record<
  string,
  ModelFramework[]
> = {
  [KnownMLModelServiceModel.TFLiteCPUDeprecated]: [ModelFramework.TFLITE],
  [KnownMLModelServiceModel.TFLiteCPU]: [ModelFramework.TFLITE],
  [KnownMLModelServiceModel.TritonModule]: [
    ModelFramework.TENSORFLOW,
    ModelFramework.PYTORCH,
  ],
  [KnownMLModelServiceModel.OnnxCPU]: [ModelFramework.ONNX],
  [KnownMLModelServiceModel.TensorFlowCPU]: [ModelFramework.TENSORFLOW],
  [KnownMLModelServiceModel.PyTorchCPU]: [ModelFramework.PYTORCH],
};

export const isMlModelAPI = (api: APITriplet) =>
  isBuiltinAPI(api, ServiceSubtypes.ML_MODEL);

export const isTFLiteCPU = (model: ModelTriplet): boolean => {
  const modelString = joinResourceTriplet(model);

  return (
    modelString === (KnownMLModelServiceModel.TFLiteCPUDeprecated as string) ||
    modelString === (KnownMLModelServiceModel.TFLiteCPU as string)
  );
};

export const isTensorFlowCPU = (model: ModelTriplet): boolean => {
  const modelString = joinResourceTriplet(model);

  return modelString === (KnownMLModelServiceModel.TensorFlowCPU as string);
};

export const isOnnxCPU = (model: ModelTriplet): boolean => {
  const modelString = joinResourceTriplet(model);

  return modelString === (KnownMLModelServiceModel.OnnxCPU as string);
};

export const isPyTorchCPU = (model: ModelTriplet): boolean =>
  joinResourceTriplet(model) ===
  (KnownMLModelServiceModel.PyTorchCPU as string);

export const filterMLModelsByServiceType = (
  registryItems: RegistryItemSummary[],
  serviceModel: ModelTriplet
) => {
  const allowedFrameworks =
    MLModelServiceTypeToAllowedFrameworks[joinResourceTriplet(serviceModel)];

  return registryItems.filter((item) => {
    if (item.metadata.oneofKind !== 'mlModelMetadata') {
      return false;
    }
    const framework = item.metadata.mlModelMetadata.modelFramework;
    /**
     * Allow the ML model registry item if we don't have an explicit list of
     * allowed frameworks for the service or the item does not specify a framework
     */
    if (
      allowedFrameworks === undefined ||
      framework === ModelFramework.UNSPECIFIED
    ) {
      return true;
    }

    // otherwise filter out models by the frameworks allowed for current service
    return allowedFrameworks.includes(framework);
  });
};
